import * as z from "zod/v4";

/**
 * Configuration for Redis stream processing (static per handler)
 * This contains settings that don't change between requests
 */
export interface RedisStreamConfig<T> {
  /**
   * Zod schema to parse and validate messages
   */
  messageSchema: z.ZodType<T>;

  /**
   * Initial polling interval when no messages are available (ms)
   * Uses adaptive polling: backs off exponentially during idle periods
   * Lower = better latency but more Redis load
   * Higher = less Redis load but higher latency
   * Default: 200ms
   */
  pollInterval?: number;

  /**
   * Maximum polling interval during idle periods (ms)
   * Adaptive polling will back off up to this limit
   * Default: 2000ms (2 seconds)
   */
  maxPollInterval?: number;

  /**
   * Maximum number of messages to read in one batch
   * Default: 64
   */
  batchSize?: number;
}

/**
 * Parameters that vary per request for Redis stream processing
 * This contains request-specific settings like stream key and start position
 */
export interface RedisStreamReadParams {
  /**
   * The Redis stream key to read from
   */
  streamKey: string;

  /**
   * Starting position in the stream
   * - "$" reads only new messages (default)
   * - "0" reads from the beginning
   * - specific ID reads from that position
   */
  startId?: string;

  /**
   * Optional abort signal for cancellation
   */
  signal?: AbortSignal;

  /**
   * Optional context for logging
   */
  logContext?: Record<string, any>;
}

/**
 * Schema for StreamMessage to be used in RPC definitions
 */
export const StreamMessageSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({ id: z.string(), data: dataSchema });

/**
 * Common interface for messages received from Redis streams
 * Contains both the Redis stream ID and the parsed message data
 */
export interface StreamMessage<T> {
  id: string; // Redis stream ID
  data: T; // Parsed message data based on schema
}
